<template>
  <div class="pharmacy-management">
    <!-- 搜索和操作区域 -->
    <div class="search-section">
      <div class="search-left">
        <el-input
            v-model="searchKeyword"
            placeholder="药品名称"
            prefix-icon="el-icon-search"
            size="small"
            class="search-input"
            @keyup.enter="handleSearch"
        />
      </div>
      <div class="search-right">
        <el-button type="success" size="small" @click="handleNewDrug">
          <i class="el-icon-plus"></i>
          新建药品
        </el-button>
      </div>
    </div>

    <!-- 统计信息 -->
    <div class="summary-info">
      共{{ totalCount }}个品种
    </div>

    <!-- 药品列表 -->
    <div class="table-section">
      <el-table
          :data="drugList"
          style="width: 100%"
          :header-cell-style="{background: '#f5f7fa', color: '#606266'}"
          border
          v-loading="loading"
      >
        <el-table-column prop="drugName" label="药名" min-width="200" align="center">
          <template slot-scope="scope">
            <span
                class="drug-name-link"
                @click="handleEditDrug(scope.row)"
            >
              {{ scope.row.drugName }}
            </span>
          </template>
        </el-table-column>

        <el-table-column prop="drugCode" label="药品编码" min-width="150" align="center">
          <template slot-scope="scope">
            <span class="drug-code">{{ scope.row.drugCode }}</span>
          </template>
        </el-table-column>

        <el-table-column prop="type" label="类型" min-width="120" align="center">
          <template slot-scope="scope">
            <span class="drug-type">{{ scope.row.type }}</span>
          </template>
        </el-table-column>

        <el-table-column prop="secondaryType" label="二级分类" min-width="120" align="center">
          <template slot-scope="scope">
            <span class="secondary-type">{{ scope.row.secondaryType || '-' }}</span>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 分页 -->
    <div class="pagination-section">
      <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="totalCount"
          class="pagination-component"
      />
    </div>

    <!-- 药品信息弹框 -->
    <el-dialog
        :title="dialogTitle"
        :visible.sync="dialogVisible"
        width="800px"
        :close-on-click-modal="false"
        custom-class="drug-info-dialog"
    >
      <div class="drug-info-content">
        <!-- 标签页 -->
        <div class="tab-section">
          <div class="tab-item active">基础</div>
        </div>

        <!-- 表单内容 -->
        <div class="form-section">
          <el-form
              :model="drugForm"
              :rules="drugRules"
              ref="drugForm"
              label-width="120px"
              class="drug-form"
          >
            <!-- 第一行：药名和商品编码 -->
            <div class="form-row">
              <el-form-item label="药名" prop="drugName" class="form-item-half">
                <el-input
                    v-model="drugForm.drugName"
                    placeholder="请输入药名"
                    class="form-input"
                />
              </el-form-item>

              <el-form-item label="商品编码" prop="productCode" class="form-item-half">
                <el-input
                    v-model="drugForm.productCode"
                    placeholder="请输入商品编码"
                    class="form-input"
                />
              </el-form-item>
            </div>

            <!-- 第二行：类型和自定义二级类型 -->
            <div class="form-row">
              <el-form-item label="类型" prop="type" class="form-item-half">
                <el-select
                    v-model="drugForm.type"
                    placeholder="请选择类型"
                    class="form-select"
                >
                  <el-option label="西药" value="西药"/>
                  <el-option label="中成药" value="中成药"/>
                  <el-option label="中药饮片" value="中药饮片"/>
                  <el-option label="医疗器械" value="医疗器械"/>
                  <el-option label="中药颗粒" value="中药颗粒"/>
                </el-select>
              </el-form-item>

              <el-form-item label="自定义二级类型" prop="secondaryType" class="form-item-half">
                <el-select
                    v-model="drugForm.secondaryType"
                    placeholder="请选择二级类型"
                    class="form-select"
                >
                  <el-option label="片剂" value="片剂"/>
                  <el-option label="胶囊" value="胶囊"/>
                  <el-option label="颗粒" value="颗粒"/>
                  <el-option label="口服液" value="口服液"/>
                  <el-option label="注射液" value="注射液"/>
                  <el-option label="软膏" value="软膏"/>
                  <el-option label="贴膏" value="贴膏"/>
                  <el-option label="散剂" value="散剂"/>
                  <el-option label="丸剂" value="丸剂"/>
                  <el-option label="糖浆" value="糖浆"/>
                </el-select>
              </el-form-item>
            </div>
          </el-form>
        </div>

        <!-- 操作按钮 -->
        <div class="action-buttons">
          <el-button
              v-if="!isEditMode"
              type="primary"
              @click="handleSave"
              :loading="saving"
              class="save-btn"
          >
            保存
          </el-button>
          <template v-else>
            <el-button
                type="danger"
                @click="handleDelete"
                :loading="deleting"
                class="delete-btn"
            >
              删除
            </el-button>
            <el-button
                type="primary"
                @click="handleUpdate"
                :loading="saving"
                class="save-btn"
            >
              保存修改
            </el-button>
          </template>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {get, post} from '@/api/api'

export default {
  name: "PharmacyManagement",
  data() {
    return {
      // 搜索相关
      searchKeyword: '',

      // 列表相关
      drugList: [],
      loading: false,
      totalCount: 0,

      // 分页相关
      currentPage: 1,
      pageSize: 14,

      // 弹框相关
      dialogVisible: false,
      isEditMode: false,
      dialogTitle: '新建药品',
      saving: false,
      deleting: false,

      // 表单数据
      drugForm: {
        id: null,
        drugName: '',
        type: '',
        secondaryType: '',
        productCode: ''
      },

      // 表单验证规则
      drugRules: {
        drugName: [
          {required: true, message: '请输入药名', trigger: 'blur'},
          {min: 1, max: 50, message: '药名长度在 1 到 50 个字符', trigger: 'blur'}
        ],
        type: [
          {required: true, message: '请选择类型', trigger: 'change'}
        ],
        secondaryType: [
          {required: false, message: '请选择二级类型', trigger: 'change'}
        ],
        productCode: [
          {required: true, message: '请输入商品编码', trigger: 'blur'},
          {pattern: /^[0-9]+$/, message: '商品编码只能包含数字', trigger: 'blur'},
          {min: 1, max: 20, message: '商品编码长度在 1 到 20 个字符', trigger: 'blur'}
        ]
      }
    }
  },

  mounted() {
    this.loadDrugList()
  },

  methods: {
    // 加载药品列表
    async loadDrugList() {
      this.loading = true
      try {
        // 模拟API调用，实际项目中替换为真实API
        const params = {
          page: this.currentPage,
          size: this.pageSize,
          keyword: this.searchKeyword
        }

        // 模拟数据
        const mockData = this.getMockData()
        this.drugList = mockData.list
        this.totalCount = mockData.total

        // 实际API调用示例：
        // const response = await get('/api/drugs', params)
        // this.drugList = response.data.list
        // this.totalCount = response.data.total

      } catch (error) {
        this.$message.error('加载药品列表失败')
        console.error('加载药品列表失败:', error)
      } finally {
        this.loading = false
      }
    },

    // 获取模拟数据
    getMockData() {
      const allDrugs = [
        {
          id: 1,
          drugName: '阿胶黄芪口服液',
          drugCode: '3786631377',
          type: '西药',
          secondaryType: '片剂',
          productCode: '3786631377'
        },
        {
          id: 2,
          drugName: '注射用乳糖酸阿奇霉素(齐宏/哈药)',
          drugCode: '3786631376',
          type: '西药',
          secondaryType: '',
          productCode: '3786631376'
        },
        {
          id: 3,
          drugName: '感冒清热颗粒',
          drugCode: '3786631354',
          type: '中成药',
          secondaryType: '',
          productCode: '3786631354'
        },
        {
          id: 4,
          drugName: '玛咖',
          drugCode: '3786631353',
          type: '中药饮片',
          secondaryType: '',
          productCode: '3786631353'
        },
        {
          id: 5,
          drugName: '枸杞',
          drugCode: '3786631352',
          type: '中药饮片',
          secondaryType: '',
          productCode: '3786631352'
        },
        {
          id: 6,
          drugName: '虚拟药品',
          drugCode: '3786631351',
          type: '医疗器械',
          secondaryType: '',
          productCode: '3786631351'
        },
        {
          id: 7,
          drugName: '虚拟药品',
          drugCode: '3786631350',
          type: '中药颗粒',
          secondaryType: '',
          productCode: '3786631350'
        },
        {
          id: 8,
          drugName: '虚拟药品',
          drugCode: '3786631349',
          type: '中药饮片',
          secondaryType: '',
          productCode: '3786631349'
        },
        {
          id: 9,
          drugName: '布洛芬混悬液(迪尔诺)',
          drugCode: '3786631347',
          type: '西药',
          secondaryType: '',
          productCode: '3786631347'
        },
        {
          id: 10,
          drugName: '罗红霉素分散片(志邦)',
          drugCode: '3786631346',
          type: '西药',
          secondaryType: '',
          productCode: '3786631346'
        },
        {
          id: 11,
          drugName: '阿莫西林克拉维酸钾片',
          drugCode: '3786631345',
          type: '西药',
          secondaryType: '',
          productCode: '3786631345'
        },
        {
          id: 12,
          drugName: '感冒灵颗粒(白云山)',
          drugCode: '3786631344',
          type: '中成药',
          secondaryType: '',
          productCode: '3786631344'
        },
        {
          id: 13,
          drugName: '愈美甲麻敏糖浆',
          drugCode: '3786631343',
          type: '',
          secondaryType: '',
          productCode: '3786631343'
        },
        {
          id: 14,
          drugName: '麸炒白术',
          drugCode: '3786631272',
          type: '中药饮片',
          secondaryType: '',
          productCode: '3786631272'
        }
      ]

      // 根据搜索关键词过滤
      let filteredDrugs = allDrugs
      if (this.searchKeyword) {
        filteredDrugs = allDrugs.filter(drug =>
            drug.drugName.includes(this.searchKeyword)
        )
      }

      // 分页
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      const list = filteredDrugs.slice(start, end)

      return {
        list,
        total: filteredDrugs.length
      }
    },

    // 搜索
    handleSearch() {
      this.currentPage = 1
      this.loadDrugList()
    },

    // 新建药品
    handleNewDrug() {
      this.isEditMode = false
      this.dialogTitle = '新建药品'
      this.drugForm = {
        id: null,
        drugName: '',
        type: '',
        secondaryType: '',
        productCode: ''
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.drugForm.clearValidate()
      })
    },

    // 编辑药品
    handleEditDrug(row) {
      this.isEditMode = true
      this.dialogTitle = '药品信息'
      this.drugForm = {
        id: row.id,
        drugName: row.drugName,
        type: row.type,
        secondaryType: row.secondaryType,
        productCode: row.productCode
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.drugForm.clearValidate()
      })
    },

    // 保存新药品
    async handleSave() {
      try {
        await this.$refs.drugForm.validate()
        this.saving = true

        // 模拟API调用
        await new Promise(resolve => setTimeout(resolve, 1000))

        // 实际API调用示例：
        // await post('/api/drugs', this.drugForm)

        this.$message.success('保存成功')
        this.dialogVisible = false
        this.loadDrugList()

      } catch (error) {
        if (error !== false) { // 不是表单验证错误
          this.$message.error('保存失败')
          console.error('保存失败:', error)
        }
      } finally {
        this.saving = false
      }
    },

    // 更新药品
    async handleUpdate() {
      try {
        await this.$refs.drugForm.validate()
        this.saving = true

        // 模拟API调用
        await new Promise(resolve => setTimeout(resolve, 1000))

        // 实际API调用示例：
        // await post(`/api/drugs/${this.drugForm.id}`, this.drugForm)

        this.$message.success('修改成功')
        this.dialogVisible = false
        this.loadDrugList()

      } catch (error) {
        if (error !== false) { // 不是表单验证错误
          this.$message.error('修改失败')
          console.error('修改失败:', error)
        }
      } finally {
        this.saving = false
      }
    },

    // 删除药品
    async handleDelete() {
      try {
        await this.$confirm('确定要删除这个药品吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        this.deleting = true

        // 模拟API调用
        await new Promise(resolve => setTimeout(resolve, 1000))

        // 实际API调用示例：
        // await post(`/api/drugs/${this.drugForm.id}/delete`)

        this.$message.success('删除成功')
        this.dialogVisible = false
        this.loadDrugList()

      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('删除失败')
          console.error('删除失败:', error)
        }
      } finally {
        this.deleting = false
      }
    },

    // 分页大小改变
    handleSizeChange(val) {
      this.pageSize = val
      this.currentPage = 1
      this.loadDrugList()
    },

    // 当前页改变
    handleCurrentChange(val) {
      this.currentPage = val
      this.loadDrugList()
    }
  }
}
</script>

<style lang="less" scoped>
.pharmacy-management {
  padding: 20px;
  background: #f5f7fa;
  min-height: 100vh;



  .search-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    padding: 16px 20px;
    background: white;
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    .search-left {
      .search-input {
        width: 300px;
      }
    }

    .search-right {
      .el-button {
        padding: 8px 16px;
        font-size: 14px;
      }
    }
  }

  .summary-info {
    margin-bottom: 16px;
    padding: 0 20px;
    font-size: 14px;
    color: #606266;
  }

  .table-section {
    background: white;
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    overflow: hidden;

    .el-table {
      width: 100% !important;

      .el-table__body-wrapper {
        width: 100%;
      }
    }

    .drug-name-link {
      color: #409eff;
      cursor: pointer;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }

    .drug-code {
      font-family: 'Courier New', monospace;
      color: #606266;
    }

    .drug-type {
      color: #606266;
    }

    .secondary-type {
      color: #909399;
    }
  }

  .pagination-section {
    margin-top: 20px;
    padding: 16px 20px;
    background: white;
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    .pagination-component {
      display: flex;
      justify-content: flex-start;
      align-items: center;

      .el-pagination__total,
      .el-pagination__sizes,
      .el-pagination__jump {
        margin-right: 20px;
      }
    }
  }

  .dialog-footer {
    text-align: right;

    .el-button {
      margin-left: 10px;
    }
  }
}

// 药品信息弹框样式
::v-deep .drug-info-dialog {
  .el-dialog {
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  }

  .el-dialog__header {
    padding: 20px 24px 0;
    border-bottom: none;

    .el-dialog__title {
      font-size: 18px;
      font-weight: 600;
      color: #303133;
    }

    .el-dialog__headerbtn {
      top: 20px;
      right: 24px;
    }
  }

  .el-dialog__body {
    padding: 0;
  }
}

.drug-info-content {
  .tab-section {
    padding: 0 24px;
    border-bottom: 1px solid #e4e7ed;

    .tab-item {
      display: inline-block;
      padding: 12px 0;
      margin-right: 24px;
      font-size: 14px;
      color: #606266;
      cursor: pointer;
      border-bottom: 2px solid transparent;
      transition: all 0.3s;

      &.active {
        color: #409eff;
        border-bottom-color: #409eff;
        font-weight: 500;
      }
    }
  }

  .form-section {
    padding: 24px;

    .drug-form {
      .form-row {
        display: flex;
        gap: 20px;
        margin-bottom: 20px;

        .form-item-half {
          flex: 1;
          margin-bottom: 0;

          .el-form-item__label {
            font-size: 14px;
            color: #606266;
            font-weight: 500;
            line-height: 32px;
            width: 100px !important;
          }

          .el-form-item__content {
            line-height: 32px;
            margin-left: 100px !important;
          }
        }
      }

      .form-input,
      .form-select {
        width: 100%;

        .el-input__inner {
          height: 36px;
          line-height: 36px;
          border-radius: 4px;
          border: 1px solid #dcdfe6;
          font-size: 14px;

          &:focus {
            border-color: #409eff;
          }
        }
      }

      .form-select {
        .el-input__inner {
          height: 36px;
          line-height: 36px;
          border-radius: 4px;
          border: 1px solid #dcdfe6;
          font-size: 14px;

          &:focus {
            border-color: #409eff;
          }
        }
      }
    }
  }

  .action-buttons {
    padding: 16px 24px 24px;
    text-align: right;
    border-top: 1px solid #f0f0f0;
    background: #fafafa;

    .el-button {
      margin-left: 12px;
      padding: 8px 20px;
      font-size: 14px;
      border-radius: 4px;

      &.save-btn {
        background: #409eff;
        border-color: #409eff;
        color: #fff;

        &:hover {
          background: #66b1ff;
          border-color: #66b1ff;
        }
      }

      &.delete-btn {
        background: #f56c6c;
        border-color: #f56c6c;
        color: #fff;

        &:hover {
          background: #f78989;
          border-color: #f78989;
        }
      }
    }
  }
}

// 响应式设计
@media (max-width: 768px) {
  .pharmacy-management {
    padding: 10px;

    .search-section {
      flex-direction: column;
      gap: 16px;

      .search-left .search-input {
        width: 100%;
      }

      .search-right {
        width: 100%;
        text-align: center;
      }
    }

    .pagination-section {
      text-align: center;
    }
  }
}
</style>